import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useShop } from '../context/ShopContext';

export const SEO = ({
  title,
  description,
  keywords,
  image,
  type = 'website',
  noindex = false
}) => {
  const location = useLocation();
  const { settings } = useShop();

  const siteUrl = typeof window !== 'undefined'
    ? window.location.origin
    : 'https://fournitures.dz';

  const currentUrl = `${siteUrl}${location.pathname}`;
  const defaultTitle = settings.storeName || 'Fournitures';
  const defaultDescription = settings.bannerText || 'متجرك الإلكتروني في الجزائر';
  const defaultImage = settings.bannerImage || `${siteUrl}/icon-512.png`;

  const finalTitle = title ? `${title} | ${defaultTitle}` : defaultTitle;
  const finalDescription = description || defaultDescription;
  const finalImage = image || defaultImage;
  const finalKeywords = keywords || 'تسوق, الجزائر, متجر إلكتروني, Fournitures, توصيل';

  useEffect(() => {
    // Update document title
    document.title = finalTitle;

    // Meta description
    updateMetaTag('description', finalDescription);
    updateMetaTag('keywords', finalKeywords);

    // Open Graph
    updateMetaTag('og:title', finalTitle, 'property');
    updateMetaTag('og:description', finalDescription, 'property');
    updateMetaTag('og:image', finalImage, 'property');
    updateMetaTag('og:url', currentUrl, 'property');
    updateMetaTag('og:type', type, 'property');
    updateMetaTag('og:locale', 'ar_DZ', 'property');
    updateMetaTag('og:site_name', defaultTitle, 'property');

    // Twitter Card
    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:title', finalTitle);
    updateMetaTag('twitter:description', finalDescription);
    updateMetaTag('twitter:image', finalImage);

    // Robots
    if (noindex) {
      updateMetaTag('robots', 'noindex, nofollow');
    } else {
      updateMetaTag('robots', 'index, follow');
    }

    // Canonical
    updateCanonicalLink(currentUrl);

    // Structured Data (JSON-LD)
    addStructuredData({
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: defaultTitle,
      url: siteUrl,
      description: finalDescription,
      potentialAction: {
        '@type': 'SearchAction',
        target: `${siteUrl}/products?search={search_term_string}`,
        'query-input': 'required name=search_term_string'
      }
    });
  }, [finalTitle, finalDescription, finalImage, currentUrl, type, noindex]);

  return null;
};

// Helper function to update or create meta tags
const updateMetaTag = (name, content, attribute = 'name') => {
  if (typeof document === 'undefined') return;

  let tag = document.querySelector(`meta[${attribute}="${name}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attribute, name);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
};

// Helper function to update canonical link
const updateCanonicalLink = (href) => {
  if (typeof document === 'undefined') return;

  let link = document.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    document.head.appendChild(link);
  }
  link.setAttribute('href', href);
};

// Helper function to add JSON-LD structured data
const addStructuredData = (data) => {
  if (typeof document === 'undefined') return;

  const existingScript = document.getElementById('jsonld-structured-data');
  if (existingScript) {
    existingScript.remove();
  }

  const script = document.createElement('script');
  script.id = 'jsonld-structured-data';
  script.type = 'application/ld+json';
  script.text = JSON.stringify(data);
  document.head.appendChild(script);
};

// Pre-defined SEO configurations for common pages
export const HomeSEO = () => (
  <SEO
    title="الرئيسية"
    description="أفضل المنتجات بأفضل الأسعار مع توصيل لجميع ولايات الجزائر"
    keywords="تسوق, الجزائر, متجر إلكتروني, Fournitures, توصيل, منتجات"
  />
);

export const ProductsSEO = ({ category }) => (
  <SEO
    title={category ? `منتجات ${category}` : 'جميع المنتجات'}
    description={category
      ? `تصفح تشكيلة ${category} مع أفضل الأسعار والتوصيل لجميع الولايات`
      : 'تصفح جميع منتجاتنا مع أفضل الأسعار والتوصيل لجميع الولايات'}
    keywords={`${category || 'منتجات'}, تسوق, الجزائر, Fournitures`}
  />
);

export const ProductDetailSEO = ({ product }) => (
  <SEO
    title={product?.name}
    description={product?.description?.substring(0, 160) || `اشترِ ${product?.name} بأفضل سعر في الجزائر`}
    image={product?.image}
    type="product"
  />
);

export const CheckoutSEO = () => (
  <SEO
    title="إتمام الطلب"
    description="أكمل طلبك الآن مع خيارات دفع متعددة وتوصيل سريع"
    noindex={true}
  />
);

export default SEO;
